import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguageStore } from '../../stores/languageStore';
import { useAuthStore } from '../../stores/authStore';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setLanguage } = useLanguageStore();
  const { user } = useAuthStore();
  const currentYear = new Date().getFullYear();

  const handleLanguageChange = (newLanguage: string) => {
    const selectedLanguage = newLanguage as any;
    
    // Update UI language immediately
    setLanguage(selectedLanguage);
    
    // If user is authenticated, also update their preferred language in the backend
    if (user) {
      const { changeLanguage } = useAuthStore.getState();
      changeLanguage(selectedLanguage).catch(error => {
        console.error('Failed to save language preference to server:', error);
        // Language switching still works in UI even if backend save fails
      });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto py-8 sm:py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          {/* Brand Section */}
          <div className="space-y-6 sm:space-y-8 xl:col-span-1 mb-8 xl:mb-0">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-cyan-400 font-brand">tvia.ai</h3>
              <p className="text-gray-300 text-sm mt-2">
                {t('footer.tagline', 'AI-powered legal solutions for everyone')}
              </p>
            </div>
            <div className="flex space-x-6">
              {/* Social Media Links */}
              <a 
                href="https://twitter.com/tvia_ai" 
                className="text-gray-400 hover:text-gray-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a 
                href="https://linkedin.com/company/tvia-ai" 
                className="text-gray-400 hover:text-gray-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a 
                href="https://github.com/tvia-ai" 
                className="text-gray-400 hover:text-gray-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Links Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              {/* Platform Links */}
              <div className="mb-8 md:mb-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  {t('footer.platform', 'Platform')}
                </h3>
                <ul className="mt-4 space-y-3 sm:space-y-4">
                  <li>
                    <Link to="/file-lawsuit" className="text-base text-gray-300 hover:text-white">
                      {t('footer.fileLawsuit', 'File a Lawsuit')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/class-actions" className="text-base text-gray-300 hover:text-white">
                      {t('footer.classActions', 'Class Actions')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal-documents" className="text-base text-gray-300 hover:text-white">
                      {t('footer.legalDocuments', 'Legal Documents')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-base text-gray-300 hover:text-white">
                      {t('footer.pricing', 'Pricing')}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Support Links */}
              <div className="mb-8 md:mb-0 md:mt-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  {t('footer.support', 'Support')}
                </h3>
                <ul className="mt-4 space-y-3 sm:space-y-4">
                  <li>
                    <Link to="/help" className="text-base text-gray-300 hover:text-white">
                      {t('footer.helpCenter', 'Help Center')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-base text-gray-300 hover:text-white">
                      {t('footer.faq', 'FAQ')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-base text-gray-300 hover:text-white">
                      {t('footer.contact', 'Contact Us')}
                    </Link>
                  </li>
                  <li>
                                    <Link to="/terms" className="text-base text-gray-300 hover:text-white">
                  {t('footer.legalDisclaimer', 'Legal Disclaimer')}
                </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="md:grid md:grid-cols-2 md:gap-8">
              {/* Company Links */}
              <div className="mb-8 md:mb-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  {t('footer.company', 'Company')}
                </h3>
                <ul className="mt-4 space-y-3 sm:space-y-4">
                  <li>
                    <Link to="/about" className="text-base text-gray-300 hover:text-white">
                      {t('footer.about', 'About Us')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/careers" className="text-base text-gray-300 hover:text-white">
                      {t('footer.careers', 'Careers')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog" className="text-base text-gray-300 hover:text-white">
                      {t('footer.blog', 'Blog')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/press" className="text-base text-gray-300 hover:text-white">
                      {t('footer.press', 'Press')}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Legal Links */}
              <div className="md:mt-0">
                <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                  {t('footer.legal', 'Legal')}
                </h3>
                <ul className="mt-4 space-y-3 sm:space-y-4">
                  <li>
                    <Link to="/privacy" className="text-base text-gray-300 hover:text-white">
                      {t('footer.privacy', 'Privacy Policy')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" className="text-base text-gray-300 hover:text-white">
                      {t('footer.terms', 'Terms of Service')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookies" className="text-base text-gray-300 hover:text-white">
                      {t('footer.cookies', 'Cookie Policy')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/gdpr" className="text-base text-gray-300 hover:text-white">
                      {t('footer.gdpr', 'GDPR Compliance')}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 sm:mt-12 border-t border-gray-700 pt-6 sm:pt-8">
          <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6 md:gap-0">
            <div className="md:order-1">
              <p className="text-sm sm:text-base text-gray-400 text-center md:text-left">
                © {currentYear} <span className="font-brand">tvia.ai</span>. {t('footer.allRightsReserved', 'All rights reserved.')}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mt-2 text-center md:text-left">
                {t('footer.disclaimer', 'This platform provides AI-assisted legal guidance and should not replace professional legal advice.')}
              </p>
            </div>
            <div className="flex justify-center md:justify-end md:order-2">
              {/* Language Selector */}
              <select
                value={i18n.language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm min-h-[44px] min-w-[120px]"
                aria-label={t('footer.selectLanguage', 'Select Language')}
              >
                <option value="en">English</option>
                <option value="he">עברית</option>
                <option value="ar">العربية</option>
                <option value="de">Deutsch</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 